import type { Message, AttendEvent, WidgetConfig } from '../types';

export interface WidgetState {
  config: WidgetConfig | null;
  sessionToken: string | null;
  conversationId: string | null;
  messages: Message[];
  isOpen: boolean;
  isVisible: boolean;
  unreadCount: number;
  agentTyping: boolean;
  sessionStarted: boolean;
  online: boolean;
  /** [B4-13] flag para desabilitar botão de envio enquanto request está em andamento */
  isSending: boolean;
}

type Listener<T> = (value: T) => void;

export class WidgetStore {
  private state: WidgetState = {
    config: null,
    sessionToken: null,
    conversationId: null,
    messages: [],
    isOpen: false,
    isVisible: true,
    unreadCount: 0,
    agentTyping: false,
    sessionStarted: false,
    online: false,
    isSending: false,
  };

  private listeners = new Map<string, Set<Listener<unknown>>>();
  private eventListeners = new Map<AttendEvent, Set<(...args: unknown[]) => void>>();

  getState(): Readonly<WidgetState> { return this.state; }

  setState(patch: Partial<WidgetState>): void {
    this.state = { ...this.state, ...patch };
    for (const key of Object.keys(patch) as (keyof WidgetState)[]) {
      this.notify(key, this.state[key]);
    }
  }

  on<K extends keyof WidgetState>(key: K, listener: Listener<WidgetState[K]>): () => void {
    if (!this.listeners.has(key)) this.listeners.set(key, new Set());
    this.listeners.get(key)!.add(listener as Listener<unknown>);
    return () => this.listeners.get(key)?.delete(listener as Listener<unknown>);
  }

  private notify(key: string, value: unknown): void {
    this.listeners.get(key)?.forEach(fn => fn(value));
  }

  addMessage(msg: Message): void {
    this.setState({ messages: [...this.state.messages, msg] });
  }

  incrementUnread(): void {
    const count = this.state.unreadCount + 1;
    this.setState({ unreadCount: count });
    this.emit('unread', count);
  }

  resetUnread(): void {
    this.setState({ unreadCount: 0 });
  }

  onEvent(event: AttendEvent, cb: (...args: unknown[]) => void): void {
    if (!this.eventListeners.has(event)) this.eventListeners.set(event, new Set());
    this.eventListeners.get(event)!.add(cb);
  }

  offEvent(event: AttendEvent, cb: (...args: unknown[]) => void): void {
    this.eventListeners.get(event)?.delete(cb);
  }

  emit(event: AttendEvent, ...args: unknown[]): void {
    this.eventListeners.get(event)?.forEach(fn => fn(...args));
  }
}
